import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ContentChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  TableColumn,
  SortEvent,
  PageEvent,
  TablePreferences,
  PAGE_SIZE_OPTIONS,
  PageSizeOption,
  DEFAULT_PAGE_SIZE,
  STORAGE_KEY_PREFIX
} from './tabela-paginada-primary.models';

@Component({
  selector: 'app-tabela-paginada-primary',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tabela-paginada-primary.component.html',
  styleUrls: ['./tabela-paginada-primary.component.scss']
})
export class TabelaPaginadaPrimaryComponent implements OnInit, OnDestroy, OnChanges {
  // ==================== INPUTS ====================

  /** Identificador único da tabela para salvar preferências */
  @Input() tableId: string = 'default';

  /** Configuração das colunas */
  @Input() columns: TableColumn[] = [];

  /** Dados a serem exibidos */
  @Input() data: any[] = [];

  /** Total de registros no backend (para paginação server-side) */
  @Input() totalRecords: number = 0;

  /** Página atual (1-based) */
  @Input() currentPage: number = 1;

  /** Se está carregando dados */
  @Input() loading: boolean = false;

  /** Se mostra o seletor de quantidade por página */
  @Input() showPageSizeSelector: boolean = true;

  /** Se mostra informação de registros */
  @Input() showRecordsInfo: boolean = true;

  /** Função para determinar classe da linha (para status) */
  @Input() rowClassFn?: (row: any) => string;

  /** Campo para ordenação inicial */
  @Input() defaultSortField?: string;

  /** Direção da ordenação inicial */
  @Input() defaultSortDirection: 'asc' | 'desc' = 'asc';

  /** Texto quando não há dados */
  @Input() emptyMessage: string = 'Nenhum registro encontrado';

  /** Texto do subtítulo quando não há dados */
  @Input() emptySubMessage: string = 'Tente ajustar os filtros ou adicione um novo registro';

  /** Número máximo de botões de página a exibir */
  @Input() maxPageButtons: number = 5;

  /** Se usa paginação do servidor (emite evento ao mudar página) */
  @Input() serverSidePagination: boolean = true;

  // ==================== OUTPUTS ====================

  /** Emitido quando a ordenação muda */
  @Output() sortChange = new EventEmitter<SortEvent>();

  /** Emitido quando a página muda */
  @Output() pageChange = new EventEmitter<PageEvent>();

  /** Emitido quando o tamanho da página muda */
  @Output() pageSizeChange = new EventEmitter<number>();

  /** Emitido quando uma linha é clicada */
  @Output() rowClick = new EventEmitter<any>();

  // ==================== TEMPLATES ====================

  /** Template para células customizadas */
  @ContentChild('cellTemplate') cellTemplate?: TemplateRef<any>;

  /** Template para ações */
  @ContentChild('actionsTemplate') actionsTemplate?: TemplateRef<any>;

  /** Template para empty state */
  @ContentChild('emptyTemplate') emptyTemplate?: TemplateRef<any>;

  // ==================== STATE ====================

  pageSizeOptions = [...PAGE_SIZE_OPTIONS];
  pageSize: PageSizeOption = DEFAULT_PAGE_SIZE;
  currentSort?: SortEvent;

  // ==================== SELETOR DE COLUNAS ====================

  seletorColunasAberto = false;
  colunasOrdenadas: TableColumn[] = [];
  private colunasOcultasSet = new Set<string>();
  private dragField: string | null = null;
  dragOverIndex: number = -1;

  get colunasVisiveis(): TableColumn[] {
    return this.colunasOrdenadas.filter(c => !this.colunasOcultasSet.has(c.field));
  }

  colunaOculta(field: string): boolean {
    return this.colunasOcultasSet.has(field);
  }

  get totalColunasVisiveis(): number {
    return this.colunasOrdenadas.filter(c => !this.colunasOcultasSet.has(c.field)).length;
  }

  toggleSeletorColunas(): void {
    this.seletorColunasAberto = !this.seletorColunasAberto;
    this.dragOverIndex = -1;
  }

  fecharSeletorColunas(): void {
    this.seletorColunasAberto = false;
    this.dragOverIndex = -1;
  }

  toggleColuna(field: string): void {
    if (this.colunasOcultasSet.has(field)) {
      this.colunasOcultasSet.delete(field);
    } else {
      if (this.totalColunasVisiveis <= 1) return;
      this.colunasOcultasSet.add(field);
    }
    this.savePreferences();
  }

  restaurarPadrao(): void {
    this.colunasOrdenadas = [...this.columns];
    this.colunasOcultasSet.clear();
    this.savePreferences();
  }

  moverColuna(field: string, direcao: 'up' | 'down'): void {
    const idx = this.colunasOrdenadas.findIndex(c => c.field === field);
    if (idx === -1) return;
    const novoIdx = direcao === 'up' ? idx - 1 : idx + 1;
    if (novoIdx < 0 || novoIdx >= this.colunasOrdenadas.length) return;
    const colunas = [...this.colunasOrdenadas];
    [colunas[idx], colunas[novoIdx]] = [colunas[novoIdx], colunas[idx]];
    this.colunasOrdenadas = colunas;
    this.savePreferences();
  }

  onDragStart(event: DragEvent, field: string): void {
    this.dragField = field;
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.dragOverIndex = index;
  }

  onDragLeave(): void { this.dragOverIndex = -1; }

  onDrop(event: DragEvent, targetIndex: number): void {
    event.preventDefault();
    this.dragOverIndex = -1;
    if (!this.dragField) return;
    const sourceIndex = this.colunasOrdenadas.findIndex(c => c.field === this.dragField);
    if (sourceIndex === -1 || sourceIndex === targetIndex) return;
    const colunas = [...this.colunasOrdenadas];
    const [moved] = colunas.splice(sourceIndex, 1);
    colunas.splice(targetIndex, 0, moved);
    this.colunasOrdenadas = colunas;
    this.dragField = null;
    this.savePreferences();
  }

  onDragEnd(): void { this.dragField = null; this.dragOverIndex = -1; }

  private inicializarColunas(): void {
    if (!this.columns || this.columns.length === 0) {
      this.colunasOrdenadas = [];
      this.colunasOcultasSet.clear();
      return;
    }
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (stored) {
        const prefs: TablePreferences = JSON.parse(stored);
        if (prefs.colunasOrdem && prefs.colunasOrdem.length > 0) {
          const map = new Map(this.columns.map(c => [c.field, c]));
          const ordenadas: TableColumn[] = [];
          for (const field of prefs.colunasOrdem) {
            if (map.has(field)) { ordenadas.push(map.get(field)!); map.delete(field); }
          }
          // Colunas novas (ausentes da ordem salva) entram na posição definida pelo array
          // original — logo após sua vizinha anterior — em vez de irem para o fim da tabela.
          map.forEach(c => {
            const idxOriginal = this.columns.findIndex(col => col.field === c.field);
            let insertAt = ordenadas.length;
            for (let i = idxOriginal - 1; i >= 0; i--) {
              const pos = ordenadas.findIndex(o => o.field === this.columns[i].field);
              if (pos >= 0) { insertAt = pos + 1; break; }
            }
            ordenadas.splice(insertAt, 0, c);
          });
          this.colunasOrdenadas = ordenadas;
        } else {
          this.colunasOrdenadas = [...this.columns];
        }
        this.colunasOcultasSet.clear();
        if (prefs.colunasOcultas && prefs.colunasOcultas.length > 0) {
          const totalOcultas = prefs.colunasOcultas.filter(f => this.colunasOrdenadas.some(c => c.field === f)).length;
          if (totalOcultas < this.colunasOrdenadas.length) {
            prefs.colunasOcultas.forEach(f => this.colunasOcultasSet.add(f));
          }
        }
      } else {
        this.colunasOrdenadas = [...this.columns];
        this.colunasOcultasSet.clear();
      }
    } catch (e) {
      this.colunasOrdenadas = [...this.columns];
      this.colunasOcultasSet.clear();
    }
  }

  // ==================== COMPUTED ====================

  /** Total de páginas */
  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize) || 1;
  }

  /** Dados paginados para exibição (para paginação local) */
  get displayedData(): any[] {
    if (this.serverSidePagination) {
      return this.data;
    }
    const start = (this.currentPage - 1) * this.pageSize;
    return this.data.slice(start, start + this.pageSize);
  }

  /** Array de números de página para exibição */
  get pageNumbers(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    const current = this.currentPage;
    const maxButtons = this.maxPageButtons;

    if (total <= maxButtons) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      const half = Math.floor(maxButtons / 2);
      let start = Math.max(1, current - half);
      let end = Math.min(total, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  /** Informação de registros exibidos */
  get recordsInfo(): string {
    if (this.totalRecords === 0) return 'Nenhum registro';

    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);

    return `${start} - ${end} de ${this.totalRecords} registros`;
  }

  /** Se pode ir para página anterior */
  get canGoPrevious(): boolean {
    return this.currentPage > 1;
  }

  /** Se pode ir para próxima página */
  get canGoNext(): boolean {
    return this.currentPage < this.totalPages;
  }

  // ==================== LIFECYCLE ====================

  ngOnInit(): void {
    this.loadPreferences();
    this.inicializarColunas();

    if (this.defaultSortField) {
      this.currentSort = {
        field: this.defaultSortField,
        direction: this.defaultSortDirection
      };
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !this.serverSidePagination) {
      this.totalRecords = this.data.length;
    }
    if (changes['totalRecords'] && this.currentPage > this.totalPages) {
      this.currentPage = 1;
    }
    if (changes['columns'] && !changes['columns'].firstChange) {
      this.inicializarColunas();
    }
  }

  ngOnDestroy(): void {
    this.savePreferences();
  }

  // ==================== PREFERENCES ====================

  private getStorageKey(): string {
    return `${STORAGE_KEY_PREFIX}${this.tableId}`;
  }

  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (stored) {
        const prefs: TablePreferences = JSON.parse(stored);
        this.pageSize = prefs.pageSize as PageSizeOption || DEFAULT_PAGE_SIZE;
        if (prefs.defaultSort) {
          this.currentSort = prefs.defaultSort;
        }
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências da tabela:', e);
    }
  }

  private savePreferences(): void {
    try {
      const prefs: TablePreferences = {
        pageSize: this.pageSize,
        defaultSort: this.currentSort,
        colunasOcultas: [...this.colunasOcultasSet],
        colunasOrdem: this.colunasOrdenadas.map(c => c.field)
      };
      localStorage.setItem(this.getStorageKey(), JSON.stringify(prefs));
    } catch (e) {
      console.warn('Erro ao salvar preferências da tabela:', e);
    }
  }

  // ==================== SORTING ====================

  onHeaderClick(column: TableColumn): void {
    if (column.sortable === false) return;

    let direction: 'asc' | 'desc' = 'asc';

    if (this.currentSort?.field === column.field) {
      direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
    }

    this.currentSort = { field: column.field, direction };
    this.savePreferences();
    this.sortChange.emit(this.currentSort);
  }

  getSortIcon(column: TableColumn): 'asc' | 'desc' | 'none' {
    if (column.sortable === false) return 'none';
    if (this.currentSort?.field !== column.field) return 'none';
    return this.currentSort.direction;
  }

  isSortable(column: TableColumn): boolean {
    return column.sortable !== false;
  }

  // ==================== PAGINATION ====================

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize as PageSizeOption;
    this.currentPage = 1;
    this.savePreferences();
    this.pageSizeChange.emit(this.pageSize);
    this.emitPageChange();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    if (page === this.currentPage) return;

    this.currentPage = page;
    this.emitPageChange();
  }

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages);
  }

  private emitPageChange(): void {
    const event: PageEvent = {
      page: this.currentPage,
      pageSize: this.pageSize,
      totalRecords: this.totalRecords,
      sort: this.currentSort
    };
    this.pageChange.emit(event);
  }

  /** Reseta a paginação para o início */
  resetPagination(): void {
    this.currentPage = 1;
  }

  // ==================== ROW HANDLING ====================

  getRowClass(row: any): string {
    if (this.rowClassFn) {
      return this.rowClassFn(row);
    }
    return '';
  }

  onRowClick(row: any): void {
    this.rowClick.emit(row);
  }

  // ==================== CELL FORMATTING ====================

  getCellValue(row: any, column: TableColumn): any {
    const value = this.getNestedValue(row, column.field);

    if (column.formatter) {
      return column.formatter(value, row);
    }

    return value;
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  formatValue(value: any, column: TableColumn): string {
    if (value === null || value === undefined) return '-';

    switch (column.type) {
      case 'date':
        return this.formatDate(value);
      case 'datetime':
        return this.formatDateTime(value);
      case 'number':
        return this.formatNumber(value);
      case 'currency':
        return this.formatCurrency(value);
      case 'boolean':
        return value ? 'Sim' : 'Não';
      default:
        return String(value);
    }
  }

  private formatDate(value: any): string {
    if (!value) return '-';
    try {
      const date = new Date(value);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return String(value);
    }
  }

  private formatDateTime(value: any): string {
    if (!value) return '-';
    try {
      const date = new Date(value);
      return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return String(value);
    }
  }

  private formatNumber(value: any): string {
    if (typeof value !== 'number') return String(value);
    return value.toLocaleString('pt-BR');
  }

  private formatCurrency(value: any): string {
    if (typeof value !== 'number') return String(value);
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  // ==================== COLUMN STYLES ====================

  getColumnStyle(column: TableColumn): { [key: string]: string } {
    const style: { [key: string]: string } = {};

    if (column.width) {
      style['width'] = column.width;
      style['min-width'] = column.width;
    }

    if (column.align) {
      style['text-align'] = column.align;
    }

    return style;
  }

  getCellClass(column: TableColumn): string {
    return column.cellClass || '';
  }
}
